import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HiX } from 'react-icons/hi';
import apiClient from '../api/client';
import ModalOverlay from './ModalOverlay';

function buildCampaignName(row) {
  const v = (row.vendor_code || '').trim();
  const t = (row.title || '').trim();
  return (v + (v && t ? ' ' : '') + t).trim() || `nm ${row.nm_id}`;
}

function deriveCampaignName(rows) {
  if (!rows.length) return '';
  if (rows.length === 1) return buildCampaignName(rows[0]);
  return rows.map((r) => r.vendor_code || r.nm_id).join(', ');
}

function parseNmIdsInput(text) {
  return [...new Set(
    String(text || '')
      .split(/[\s,;]+/)
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0)
  )];
}

export default function CreateCampaignModal({ open, onClose, initialRows = [], clientId, onSuccess }) {
  const [bidType, setBidType] = useState('manual');
  const [paymentType, setPaymentType] = useState('cpm');
  const [placementTypes, setPlacementTypes] = useState(['search', 'recommendations']);
  const [mode, setMode] = useState('one');
  const [campaignName, setCampaignName] = useState('');
  const [campaignNameTouched, setCampaignNameTouched] = useState(false);
  const campaignNameTouchedRef = useRef(false);
  const [manualNmInput, setManualNmInput] = useState('');
  const [manualCardRows, setManualCardRows] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null); // { processed, total }
  const fetchSeqRef = useRef(0);
  const pollTimerRef = useRef(null);
  const lastCreatedRef = useRef(0);

  const parsedNmIds = useMemo(() => parseNmIdsInput(manualNmInput), [manualNmInput]);
  const manualMode = initialRows.length === 0;
  const effectiveRows = initialRows.length > 0 ? initialRows : manualCardRows;
  const single = effectiveRows.length <= 1;

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setError('');
    setProgress(null);
    setManualNmInput('');
    setManualCardRows([]);
    setCampaignNameTouched(false);
    campaignNameTouchedRef.current = false;
    setLoadingCards(false);
    if (initialRows.length === 0) {
      setCampaignName('');
      setMode('one');
      return;
    }
    setCampaignName(deriveCampaignName(initialRows));
    setMode('one');
  }, [open, initialRows]);

  useEffect(() => () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
  }, []);

  useEffect(() => {
    if (!open || !manualMode || !clientId) return undefined;

    const nmIds = parsedNmIds;
    if (!nmIds.length) {
      setManualCardRows([]);
      if (!campaignNameTouchedRef.current) setCampaignName('');
      return undefined;
    }

    const seq = ++fetchSeqRef.current;
    const timer = setTimeout(async () => {
      setLoadingCards(true);
      try {
        const { data } = await apiClient.get('/reports/cards-by-nm', {
          params: {
            client_id: clientId,
            nm_ids: nmIds.join(','),
          },
        });
        if (seq !== fetchSeqRef.current) return;
        const cards = Array.isArray(data?.cards) ? data.cards : [];
        const byNm = new Map(cards.map((c) => [Number(c.nm_id), c]));
        const rows = nmIds.map((nm_id) => {
          const card = byNm.get(nm_id);
          return card
            ? {
                nm_id,
                vendor_code: card.vendor_code || '',
                title: card.title || '',
              }
            : { nm_id, vendor_code: '', title: '' };
        });
        setManualCardRows(rows);
        if (!campaignNameTouchedRef.current) {
          setCampaignName(deriveCampaignName(rows));
        }
      } catch {
        if (seq !== fetchSeqRef.current) return;
        const fallbackRows = nmIds.map((nm_id) => ({ nm_id, vendor_code: '', title: '' }));
        setManualCardRows(fallbackRows);
        if (!campaignNameTouchedRef.current) {
          setCampaignName(deriveCampaignName(fallbackRows));
        }
      } finally {
        if (seq === fetchSeqRef.current) setLoadingCards(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [open, manualMode, clientId, parsedNmIds]);

  const togglePlacement = (t) => {
    setPlacementTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  };

  const pollJobStatus = (jobId) => {
    const tick = async () => {
      try {
        const { data, status: httpStatus } = await apiClient.get(
          `/reports/create-seacat-ad/status/${jobId}`,
          {
            params: { client_id: Number(clientId), _t: Date.now() },
            headers: {
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
            },
            // axios/браузер иначе могут получить 304 без body и «завершить» опрос
            validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
          },
        );

        // 304 / пустой ответ — повторяем, задача ещё может идти
        if (httpStatus === 304 || !data || typeof data !== 'object' || !data.status) {
          pollTimerRef.current = setTimeout(tick, 2000);
          return;
        }

        setProgress({ processed: data.processed, total: data.total });
        setResult(data);

        // Инкрементально обновляем список кампаний, как только появляются новые
        if (data.created > lastCreatedRef.current) {
          lastCreatedRef.current = data.created;
          onSuccess?.();
        }

        if (data.status === 'running') {
          pollTimerRef.current = setTimeout(tick, 2000);
          return;
        }

        // Задача завершена
        setSubmitting(false);
        onSuccess?.();
        const hasErrors = (data.errors || []).length > 0;
        const hasWarnings = (data.warnings || []).length > 0;
        if (data.status === 'failed') {
          setError(data.error || 'Не удалось создать кампании');
        } else if (data.created > 0 && !hasErrors && !hasWarnings) {
          setTimeout(() => onClose(), 1500);
        }
      } catch (err) {
        setSubmitting(false);
        setError(err.response?.data?.message || err.message || 'Ошибка получения статуса создания');
      }
    };
    tick();
  };

  const handleSubmit = async () => {
    if (!clientId) return;
    const rows = effectiveRows;
    if (rows.length === 0) {
      setError('Укажите хотя бы один артикул WB (nm_id)');
      return;
    }

    setSubmitting(true);
    setError('');
    setResult(null);
    setProgress(null);
    lastCreatedRef.current = 0;
    try {
      let campaigns;
      if (mode === 'one' || single) {
        const name = (campaignName || '').trim() || deriveCampaignName(rows);
        campaigns = [{ name, nms: rows.map((r) => r.nm_id) }];
      } else {
        campaigns = rows.map((r) => ({
          name: buildCampaignName(r),
          nms: [r.nm_id],
        }));
      }
      const body = {
        client_id: Number(clientId),
        campaigns,
        bid_type: bidType,
        budget: 1000,
        start_after_create: true,
        payment_type: paymentType,
      };
      if (bidType === 'manual' && placementTypes.length > 0) {
        body.placement_types = placementTypes;
      }
      const { data } = await apiClient.post('/reports/create-seacat-ad', body);
      if (data?.job_id) {
        setProgress({ processed: 0, total: data.total || campaigns.length });
        pollJobStatus(data.job_id);
      } else {
        // Обратная совместимость, если бэкенд вернул синхронный результат
        setResult(data);
        setSubmitting(false);
        onSuccess?.();
      }
    } catch (err) {
      setSubmitting(false);
      setError(err.response?.data?.message || err.message || 'Ошибка создания кампаний');
    }
  };

  if (!open) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <div
        className="modal-panel rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-header flex justify-between items-center p-4 sticky top-0 z-10 rounded-t-2xl">
          <h3 className="text-lg font-semibold text-gray-800">Создание рекламной кампании</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <HiX className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {manualMode ? (
            <div>
              <label htmlFor="manual-nm-ids" className="block text-sm font-medium text-gray-700 mb-1">
                Артикулы WB (nm_id)
              </label>
              <textarea
                id="manual-nm-ids"
                value={manualNmInput}
                onChange={(e) => {
                  setManualNmInput(e.target.value);
                  setCampaignNameTouched(false);
                  campaignNameTouchedRef.current = false;
                }}
                placeholder="Например: 123456789, 987654321"
                className="glass-input w-full text-sm font-mono min-h-[88px] resize-y"
                spellCheck={false}
              />
              <p className="text-xs text-gray-500 mt-1">
                Через запятую, пробел или с новой строки
                {parsedNmIds.length > 0 ? ` · выбрано: ${parsedNmIds.length}` : ''}
                {loadingCards ? ' · загрузка карточек…' : ''}
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              Артикулов: {initialRows.length}.{' '}
              {initialRows.length > 1 ? 'Выберите одну кампанию на все или отдельную на каждый.' : ''}
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Тип ставки</label>
            <div className="flex gap-4">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="bid_type"
                  checked={bidType === 'manual'}
                  onChange={() => setBidType('manual')}
                  className="text-indigo-600 focus:ring-indigo-500"
                />
                <span>Ручная</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="bid_type"
                  checked={bidType === 'unified'}
                  onChange={() => setBidType('unified')}
                  className="text-indigo-600 focus:ring-indigo-500"
                />
                <span>Единая</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Тип оплаты</label>
            <div className="flex gap-4">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="payment_type"
                  checked={paymentType === 'cpm'}
                  onChange={() => setPaymentType('cpm')}
                  className="text-indigo-600 focus:ring-indigo-500"
                />
                <span>За показы</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="payment_type"
                  checked={paymentType === 'cpc'}
                  onChange={() => setPaymentType('cpc')}
                  className="text-indigo-600 focus:ring-indigo-500"
                />
                <span>За клик</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">При cpc в кампании автоматически устанавливается минимальная ставка.</p>
          </div>

          {bidType === 'manual' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Места размещения</label>
              <div className="flex gap-4">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={placementTypes.includes('search')}
                    onChange={() => togglePlacement('search')}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>В поиске</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={placementTypes.includes('recommendations')}
                    onChange={() => togglePlacement('recommendations')}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>В рекомендациях</span>
                </label>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Название кампании</label>
            <input
              type="text"
              value={campaignName}
              onChange={(e) => {
                setCampaignName(e.target.value);
                setCampaignNameTouched(true);
                campaignNameTouchedRef.current = true;
              }}
              placeholder="Артикул + название"
              className="glass-input w-full text-sm"
              disabled={!single && mode === 'per'}
            />
            {!single && mode === 'per' && (
              <p className="text-xs text-gray-500 mt-0.5">Для каждого артикула подставится артикул + title.</p>
            )}
            {manualMode && parsedNmIds.length > 0 && !campaignNameTouched && (
              <p className="text-xs text-gray-500 mt-0.5">
                Название сформировано автоматически: артикул продавца + название товара
              </p>
            )}
          </div>

          {!single && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Режим создания</label>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="mode"
                    checked={mode === 'one'}
                    onChange={() => {
                      setMode('one');
                      if (!campaignNameTouchedRef.current) {
                        setCampaignName(deriveCampaignName(effectiveRows));
                      }
                    }}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>Одна кампания на все выбранные артикулы</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="mode"
                    checked={mode === 'per'}
                    onChange={() => setMode('per')}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>Отдельная кампания для каждого артикула</span>
                </label>
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}
          {submitting && progress && (
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm space-y-2">
              <p className="text-indigo-800">
                Создание в фоне: {progress.processed} из {progress.total}
                {' '}(WB ограничивает частоту создания — это может занять несколько минут)
              </p>
              <div className="w-full bg-indigo-100 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-indigo-500 h-2 transition-all"
                  style={{ width: `${progress.total ? Math.round((progress.processed / progress.total) * 100) : 0}%` }}
                />
              </div>
              <p className="text-xs text-indigo-700">
                Окно можно закрыть — создание продолжится, новые кампании появятся на вкладке «Реклама».
              </p>
            </div>
          )}
          {result && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm space-y-2">
              <p className="text-green-700">Создано кампаний: {result.created}</p>
              {result.deposited > 0 && (
                <p className="text-green-700">Пополнено на бюджет: {result.deposited}</p>
              )}
              {result.started > 0 && (
                <p className="text-green-700">Запущено: {result.started}</p>
              )}
              {result.warnings?.length > 0 && (
                <ul className="text-amber-800 list-disc list-inside">
                  {result.warnings.map((w, i) => (
                    <li key={i}>{w.name}: {w.message}</li>
                  ))}
                </ul>
              )}
              {result.errors?.length > 0 && (
                <ul className="text-red-600 list-disc list-inside">
                  {result.errors.map((e, i) => (
                    <li key={i}>{e.name}: {e.message}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer flex justify-end gap-3 p-4 sticky bottom-0 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="glass-btn-secondary px-4 py-2"
          >
            {submitting ? 'Свернуть' : 'Закрыть'}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || loadingCards}
            className="glass-btn px-4 py-2 disabled:opacity-50"
          >
            {submitting
              ? (progress ? `Создание ${progress.processed}/${progress.total}…` : 'Создание…')
              : 'Создать'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
